/**
 * AdsLanding (/lp) — a focused, mobile-first landing page for Google Ads traffic.
 * ONE conversion goal: get early access. No nav distractions, a single form,
 * the offer front-and-centre (free setup up to 4 weeks + 14-day trial), and
 * GCLID/UTM attribution wired through so booked leads upload back to Google Ads.
 */
import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, Loader2, ShieldCheck, Gift, CheckCircle2,
  LineChart, Brain, Plug, Lock, Star,
} from 'lucide-react';
import { getAdAttribution, getGclid } from '@/utils/gclid';
import ExitIntentOffer from '@/components/ExitIntentOffer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Ads-only gate: allow real ad clicks (carry ?gclid / utm_source), an owner
// preview (?preview=1), and anyone who already has a stored gclid. Everyone else
// (organic traffic that stumbles onto /lp) is bounced to the normal homepage so
// this page is reserved for Google Ads visitors.
function isAdsVisitor() {
  try {
    const p = new URLSearchParams(window.location.search);
    if (p.get('preview') === '1') return true;
    if (p.get('gclid') || p.get('gclsrc') || p.get('utm_source') || p.get('utm_medium')) return true;
    if (getGclid()) return true;
    return false;
  } catch {
    return true; // fail open — never block a real ad click on a parsing error
  }
}

const INTEGRATIONS = [
  { name: 'Xero', domain: 'xero.com' },
  { name: 'MYOB', domain: 'myob.com' },
  { name: 'Shopify', domain: 'shopify.com' },
  { name: 'Square', domain: 'squareup.com' },
  { name: 'Stripe', domain: 'stripe.com' },
  { name: 'HubSpot', domain: 'hubspot.com' },
];

const VALUE_PROPS = [
  { icon: LineChart, title: 'Real-time dashboards', body: 'See cash flow, sales and stock the moment they move — not at month-end.' },
  { icon: Brain, title: 'Predictive AI forecasting', body: 'Know what’s coming and get told what to do about it, in plain English.' },
  { icon: Plug, title: '160+ integrations', body: 'Plugs straight into Xero, MYOB, Shopify, Square and the tools you already use.' },
];

const IntegrationLogo = ({ domain, name }) => {
  const [idx, setIdx] = useState(0);
  const srcs = [`https://www.google.com/s2/favicons?domain=${domain}&sz=64`];
  if (idx >= srcs.length) {
    return <span className="text-xs font-semibold text-slate-500">{name}</span>;
  }
  return <img src={srcs[idx]} alt={name} onError={() => setIdx((i) => i + 1)} className="h-6 w-auto object-contain" />;
};

export default function AdsLanding() {
  const navigate = useNavigate();
  const formRef = useRef(null);
  const [form, setForm] = useState({ first_name: '', email: '', company_name: '', phone: '', hp_url: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // The cookie banner is now delayed ~60s, so the sticky CTA no longer clashes
  // with it on load — show it right away for maximum conversion.
  const ctaReady = true;

  // Ads-only gate — bounce organic visitors to the homepage.
  useEffect(() => {
    if (!isAdsVisitor()) navigate('/', { replace: true });
  }, [navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });

  const submit = async (e) => {
    e.preventDefault();
    if (!/\S+@\S+\.\S+/.test(form.email) || !form.company_name.trim()) {
      setError('Please add your work email and business name.');
      return;
    }
    setError(''); setSubmitting(true);
    try {
      const r = await axios.post(`${API_URL}/api/waitlist/submit`, {
        first_name: form.first_name,
        email: form.email,
        company_name: form.company_name,
        phone: form.phone,
        issues_comment: 'Requested early access from Google Ads landing page.',
        hp_url: form.hp_url,
        ...getAdAttribution(),
      });
      navigate('/thank-you/waitlist', { state: {
        name: (form.first_name || '').trim().split(' ')[0],
        savings: r.data?.estimated_savings,
        waiverStatus: r.data?.waiver_status,
      } });
    } catch (err) {
      setError(err?.response?.status === 429
        ? 'You’re going a little fast — please try again in a minute.'
        : 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="ads-landing">
      <Helmet>
        <title>Corvana — AI Business Intelligence for Australian Businesses | Free Setup + 14-Day Trial</title>
        <meta name="description" content="Real-time AI business intelligence for Australian SMEs. Free setup period (up to 4 weeks) plus a 14-day free trial. 160+ integrations. No credit card required." />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      {/* Minimal, distraction-free top bar */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-slate-100">
        <div className="max-w-md mx-auto px-5 h-14 flex items-center justify-between">
          <img src="/corvana-logo-wordmark-dark.webp" alt="Corvana" className="h-7 w-auto" width="120" height="40" fetchPriority="high" data-testid="lp-logo" />
          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-500">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" /> Australian owned
          </span>
        </div>
      </header>

      <main className="max-w-md mx-auto px-5 pb-28">
        {/* Hero */}
        <section className="pt-9 text-center">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#0066CC]/10 text-[#0066CC] text-xs font-semibold mb-4" data-testid="lp-eyebrow">
              <Star className="w-3.5 h-3.5" /> Now onboarding Australian businesses
            </span>
            <h1 className="text-[2rem] leading-[1.08] font-bold tracking-tight" data-testid="lp-headline">
              Run your business on <span className="text-[#0066CC]">real-time AI</span> intelligence
            </h1>
            <p className="text-base text-slate-500 mt-4">
              Corvana connects your tools and turns your numbers into live dashboards,
              forecasts and clear next steps — built for Australian SMEs.
            </p>
          </motion.div>

          {/* Offer — front and centre */}
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.15 }}
            className="mt-6 rounded-2xl bg-gradient-to-br from-[#0066CC] to-[#007AFF] text-white p-5 text-left shadow-lg shadow-[#0066CC]/20"
            data-testid="lp-offer"
          >
            <div className="flex items-start gap-3">
              <Gift className="w-6 h-6 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-lg leading-snug">Free setup period (up to 4 weeks) + a 14-day free trial</p>
                <p className="text-white/85 text-sm mt-1">
                  We hand-configure Corvana on your real data, then you trial the whole
                  platform free for 14 days. No credit card to get started.
                </p>
                <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-sm font-semibold">
                  <ShieldCheck className="w-4 h-4" /> Setup worth up to $2,340 — free (one-off)
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Lead form — the single conversion goal */}
        <section ref={formRef} id="lead-form" className="mt-7">
          <form onSubmit={submit} className="rounded-3xl border border-slate-200 shadow-xl shadow-black/5 p-6 space-y-4 bg-white" data-testid="lp-form">
            <p className="text-center font-semibold text-lg">Get early access</p>
            {/* Honeypot */}
            <input type="text" name="contact_nick" value={form.hp_url} onChange={(e) => set('hp_url', e.target.value)}
              tabIndex={-1} autoComplete="new-password" aria-hidden="true"
              style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }} />
            <Input data-testid="lp-first-name" value={form.first_name} onChange={(e) => set('first_name', e.target.value)} placeholder="First name" className="h-12 text-base" />
            <Input data-testid="lp-email" type="email" inputMode="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="Work email *" className="h-12 text-base" />
            <Input data-testid="lp-company" value={form.company_name} onChange={(e) => set('company_name', e.target.value)} placeholder="Business name *" className="h-12 text-base" />
            <Input data-testid="lp-phone" type="tel" inputMode="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="Phone (optional)" className="h-12 text-base" />
            {error && <p className="text-sm text-rose-600" data-testid="lp-error">{error}</p>}
            <Button type="submit" disabled={submitting} data-testid="lp-submit"
              className="w-full h-14 text-base font-semibold gap-2 bg-[#0066CC] hover:bg-[#0055aa] text-white">
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Claim my free setup <ArrowRight className="w-4 h-4" /></>}
            </Button>
            <p className="flex items-center justify-center gap-1.5 text-xs text-slate-400">
              <Lock className="w-3 h-3" /> Your details are safe. No spam, unsubscribe anytime.
            </p>
          </form>
        </section>

        {/* Credibility band — CSIRO logo + compliant text-only program credit */}
        <section className="mt-9 text-center" data-testid="lp-partners">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-slate-400 mb-4">Proud partners of</p>
          <div className="flex items-center justify-center">
            <div className="flex flex-col items-center">
              <div className="h-14 flex items-center justify-center">
                <img src="/csiro-logo.webp" alt="CSIRO Data61" loading="lazy" className="max-h-11 w-auto object-contain" />
              </div>
              <span className="mt-2 text-xs font-semibold text-slate-600">CSIRO Data61</span>
              <span className="text-[11px] text-slate-400">AI Division</span>
            </div>
          </div>
          <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50/70 p-4 text-left" data-testid="lp-buy-australian-credit">
            <div className="flex items-center gap-3">
              <div className="shrink-0 flex flex-col items-center justify-center rounded-lg border border-slate-300 bg-white px-3 py-1.5">
                <span className="font-bold text-base tracking-tight text-slate-900 leading-none">NAIC</span>
                <span className="mt-0.5 text-[8px] font-semibold uppercase tracking-[0.12em] text-slate-500">National AI Centre</span>
              </div>
              <p className="text-[11px] leading-relaxed text-slate-700">
                Corvana is a <span className="font-semibold text-slate-900">National Digital Track participant</span> in the{' '}
                <span className="font-semibold text-slate-900">Buy Australian AI Partnership</span> program, delivered by
                Stone &amp; Chalk, with the National AI Centre (NAIC) as Principal Sponsor.
              </p>
            </div>
          </div>
        </section>

        {/* Trust — integrations */}
        <section className="mt-9 text-center">
          <p className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-3">Works with the tools you already use</p>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-3 grayscale opacity-80" data-testid="lp-integrations">
            {INTEGRATIONS.map((i) => <IntegrationLogo key={i.name} domain={i.domain} name={i.name} />)}
          </div>
        </section>

        {/* Value props */}
        <section className="mt-9 space-y-3" data-testid="lp-value-props">
          {VALUE_PROPS.map((v) => (
            <div key={v.title} className="flex items-start gap-3 rounded-2xl border border-slate-100 bg-slate-50/60 p-4">
              <div className="w-10 h-10 shrink-0 rounded-xl bg-[#0066CC]/10 flex items-center justify-center">
                <v.icon className="w-5 h-5 text-[#0066CC]" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">{v.title}</p>
                <p className="text-sm text-slate-500 mt-0.5">{v.body}</p>
              </div>
            </div>
          ))}
        </section>

        {/* Reassurance strip */}
        <section className="mt-9 rounded-2xl bg-slate-900 text-white p-5" data-testid="lp-reassurance">
          <div className="space-y-2.5">
            {['Setup worth up to $2,340 — free for early-access businesses',
              'We configure Corvana on your real data before you log in',
              '14-day free trial — cancel anytime, no lock-in contracts',
              'Bank-grade security · your data stays private',
              'Built and supported in Australia'].map((t) => (
              <p key={t} className="flex items-center gap-2.5 text-sm">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" /> {t}
              </p>
            ))}
          </div>
        </section>

        {/* Footer — subtle route back to the main site */}
        <div className="mt-8 text-center space-y-2">
          <a href="/" data-testid="lp-home-link"
            className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0066CC] transition-colors">
            <ArrowRight className="w-3.5 h-3.5 rotate-180" /> Explore the full corvana.com.au
          </a>
          <p className="text-xs text-slate-400">
            © {new Date().getFullYear()} Corvana ·{' '}
            <a href="/privacy" className="underline hover:text-slate-600">Privacy</a> ·{' '}
            <a href="/terms" className="underline hover:text-slate-600">Terms</a>
          </p>
        </div>
      </main>

      {/* Rescue abandoners with a one-time offer on exit */}
      <ExitIntentOffer seedEmail={form.email} seedCompany={form.company_name} />

      {/* Sticky conversion CTA — appears once the cookie banner is dismissed */}
      {ctaReady && (
        <button onClick={scrollToForm} data-testid="lp-sticky-cta"
          className="fixed bottom-0 inset-x-0 z-[9998] flex items-center justify-center gap-2 bg-[#0066CC] text-white font-semibold py-4 px-6 shadow-[0_-4px_20px_-4px_rgba(0,0,0,0.25)] active:bg-[#0055aa] transition-colors"
          style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}>
          Claim my free setup + trial <ArrowRight className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
